import { Link, useRouterState } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/ui/sidebar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Skeleton } from '@/components/ui/skeleton';
import { useMenuQuery } from '@/features/admin/queries/navegacion.queries';
import type { ModuloMenuDto } from '@/features/admin/types/menu.types';

const ADMIN_PREFIX = '/admin';

function toAdminRoute(ruta: string | null): string {
  if (!ruta) return '#';
  return `${ADMIN_PREFIX}${ruta}`;
}

function isRouteActive(route: string, pathname: string): boolean {
  if (route === '#') return false;
  return pathname === route || pathname.startsWith(`${route}/`);
}

function LetterAvatar({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' }) {
  const letter = name.trim().charAt(0).toUpperCase() || '?';
  const sizeClass = size === 'sm' ? 'size-5 text-[10px]' : 'size-6 text-xs';
  return (
    <span
      className={`bg-muted text-muted-foreground group-data-[active=true]/item:from-primary group-data-[active=true]/item:to-secondary group-data-[active=true]/item:text-primary-foreground group-data-[active=true]/item:shadow-soft flex shrink-0 items-center justify-center rounded-md border font-semibold transition-all group-data-[active=true]/item:border-transparent group-data-[active=true]/item:bg-linear-to-br ${sizeClass}`}
      aria-hidden
    >
      {letter}
    </span>
  );
}

const ACTIVE_ITEM_CLASSES =
  'group/item relative data-[active=true]:border-l-4 data-[active=true]:border-l-primary data-[active=true]:bg-linear-to-r data-[active=true]:from-primary/15 data-[active=true]:to-transparent data-[active=true]:text-primary data-[active=true]:font-semibold data-[active=true]:hover:from-primary/20 data-[active=true]:hover:to-transparent data-[active=true]:hover:text-primary';

function MenuItemWithChildren({ item }: { item: ModuloMenuDto }) {
  const pathname = useRouterState({ select: s => s.location.pathname });
  const isActive = item.hijos.some(h => h.ruta && isRouteActive(toAdminRoute(h.ruta), pathname));

  return (
    <Collapsible defaultOpen={isActive} className="group/collapsible">
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton tooltip={item.nombre} className={ACTIVE_ITEM_CLASSES}>
            <LetterAvatar name={item.nombre} />
            <span>{item.nombre}</span>
            <ChevronRight className="ml-auto size-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {item.hijos
              .sort((a, b) => a.orden - b.orden)
              .map(child => {
                const childRoute = toAdminRoute(child.ruta);
                return (
                  <SidebarMenuSubItem key={child.id}>
                    <SidebarMenuSubButton
                      asChild
                      isActive={isRouteActive(childRoute, pathname)}
                      className={ACTIVE_ITEM_CLASSES}
                    >
                      <Link to={childRoute}>
                        <LetterAvatar name={child.nombre} size="sm" />
                        <span>{child.nombre}</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                );
              })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

function MenuItemLink({ item }: { item: ModuloMenuDto }) {
  const pathname = useRouterState({ select: s => s.location.pathname });
  const route = toAdminRoute(item.ruta);

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        tooltip={item.nombre}
        isActive={isRouteActive(route, pathname)}
        className={ACTIVE_ITEM_CLASSES}
      >
        <Link to={route}>
          <LetterAvatar name={item.nombre} />
          <span>{item.nombre}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function SidebarSkeleton() {
  return (
    <div className="space-y-4 p-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      ))}
    </div>
  );
}

export function AdminSidebar() {
  const { data: menu, isLoading } = useMenuQuery();

  return (
    <Sidebar collapsible="icon" variant="sidebar">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to="/admin">
                <div className="bg-sidebar-primary flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg">
                  <img
                    src="/apeps-isotipo.png"
                    alt="APEPS"
                    className="size-5 object-contain brightness-0 invert"
                  />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="text-sidebar-foreground truncate font-semibold">APEPS</span>
                  <span className="text-sidebar-foreground/50 truncate text-xs">Admin</span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {isLoading ? (
          <SidebarSkeleton />
        ) : (
          menu
            ?.sort((a, b) => a.orden - b.orden)
            .map(modulo => (
              <SidebarGroup key={modulo.id}>
                <SidebarGroupLabel>{modulo.nombre}</SidebarGroupLabel>
                <SidebarMenu className="gap-1.5">
                  {modulo.hijos.length > 0 ? (
                    modulo.hijos
                      .sort((a, b) => a.orden - b.orden)
                      .map(item =>
                        item.hijos.length > 0 ? (
                          <MenuItemWithChildren key={item.id} item={item} />
                        ) : (
                          <MenuItemLink key={item.id} item={item} />
                        )
                      )
                  ) : modulo.ruta ? (
                    <MenuItemLink item={modulo} />
                  ) : null}
                </SidebarMenu>
              </SidebarGroup>
            ))
        )}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="sm" asChild>
              <Link to="/" className="text-muted-foreground hover:text-foreground">
                <span className="text-xs">Volver al sitio</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
